import { DecimalPipe } from '@angular/common';
import {
  ChangeDetectionStrategy,
  Component,
  computed,
  effect,
  inject,
  input,
  InputSignal,
  Signal,
  signal,
  WritableSignal
} from '@angular/core';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { filter, take } from 'rxjs';
import {
  SZ_SCORING_LABELS,
  SzButtonComponent,
  SzCardComponent,
  SzConfirmDialogService,
  SzHfGridComponent,
  SzHfTime,
  SzHfZone,
  SzHfZoneKey,
  SzIconComponent,
  SzSectionHeadComponent
} from '@ui';
import { RunCommandHandler } from '../../../application/handlers';
import {
  allocateShotTo,
  computeHitFactor,
  countByZone,
  initShotAllocation,
  zonesToHitFactorInput
} from '../../../application/hit-factor.util';
import { RunViewModel, SessionDrillViewModel } from '../../../application/models';
import { RunInput } from '../../../application/state';
import { NumpadSheetComponent } from '../../numpad-sheet/numpad-sheet.component';
import { NumpadSheetContext, NumpadSheetMode, NumpadSheetVariant } from '../../numpad-sheet/numpad-sheet.types';
import { SessionBestBlockComponent } from '../../session-best-block/session-best-block.component';
import { SessionBestFocal, SessionBestTile } from '../../session-best-block/session-best-block.types';
import { SessionRepListComponent } from '../../session-rep-list/session-rep-list.component';
import { buildRunRows } from '../../session-rep-list/run-rows.util';
import { SessionRunRow } from '../../session-rep-list/session-rep-list.types';

@Component({
  selector: 'lib-hit-factor-run-entry',
  templateUrl: './hit-factor-run-entry.component.html',
  styleUrls: ['./hit-factor-run-entry.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: true,
  imports: [
    DecimalPipe,
    TranslateModule,
    NumpadSheetComponent,
    SessionBestBlockComponent,
    SessionRepListComponent,
    SzButtonComponent,
    SzCardComponent,
    SzHfGridComponent,
    SzIconComponent,
    SzSectionHeadComponent
  ]
})
export class HitFactorRunEntryComponent {
  private readonly runHandler: RunCommandHandler = inject(RunCommandHandler);
  private readonly translate: TranslateService = inject(TranslateService);
  private readonly confirmDialog: SzConfirmDialogService = inject(SzConfirmDialogService);

  readonly sessionDrill: InputSignal<SessionDrillViewModel> = input.required<SessionDrillViewModel>();

  private readonly currentZones: WritableSignal<readonly SzHfZoneKey[]> = signal<readonly SzHfZoneKey[]>([]);
  private readonly currentTimeSeconds: WritableSignal<number | null> = signal<number | null>(null);
  readonly editingRun: WritableSignal<RunViewModel | null> = signal<RunViewModel | null>(null);

  readonly timeSheetOpen: WritableSignal<boolean> = signal<boolean>(false);
  readonly timeSheetResetTrigger: WritableSignal<number> = signal<number>(0);

  readonly timeSheetVariant: Signal<NumpadSheetVariant> = computed(() => 'hf-time' as const);
  readonly timeSheetMode: Signal<NumpadSheetMode> = computed(() => 'add' as const);

  readonly timeSheetInitialPayload: Signal<RunInput | null> = computed((): RunInput | null => {
    const t: number | null = this.currentTimeSeconds();
    return t === null ? null : { timeSeconds: t };
  });

  readonly timeSheetContext: Signal<NumpadSheetContext> = computed((): NumpadSheetContext => {
    const sd: SessionDrillViewModel = this.sessionDrill();
    return {
      drillName: sd.drillName,
      runNumber: sd.runs.length + 1,
      targetRuns: sd.targetRuns,
      secondaryLine: SZ_SCORING_LABELS[sd.scoring]
    };
  });

  readonly focal: Signal<SessionBestFocal | null> = computed(() => null);

  readonly tiles: Signal<readonly SessionBestTile[]> = computed((): readonly SessionBestTile[] => {
    const sd: SessionDrillViewModel = this.sessionDrill();
    const hfs: readonly number[] = sd.runs
      .map((r: RunViewModel) => r.hitFactor)
      .filter((v: number | null): v is number => v !== null);
    const best: number | null = hfs.length === 0 ? null : Math.max(...hfs);
    const avg: number | null = hfs.length === 0 ? null : hfs.reduce((a: number, b: number) => a + b, 0) / hfs.length;
    return [
      { label: 'Best HF', value: best === null ? '-' : best.toFixed(2), unit: null, variant: 'default' },
      { label: 'Avg', value: avg === null ? '-' : avg.toFixed(2), unit: null, variant: 'default' },
      { label: 'Runs', value: sd.runs.length, unit: null, variant: 'lime' }
    ];
  });

  readonly currentZoneGrid: Signal<readonly SzHfZone[]> = computed((): readonly SzHfZone[] => {
    const counts: Readonly<Record<SzHfZoneKey, number>> = countByZone(this.currentZones());
    return [
      { key: 'A', value: counts.A },
      { key: 'C', value: counts.C },
      { key: 'D', value: counts.D },
      { key: 'M', value: counts.M },
      { key: 'NS', value: counts.NS }
    ];
  });

  readonly currentTimeLabel: Signal<string | null> = computed(() => {
    const t: number | null = this.currentTimeSeconds();
    return t === null ? null : t.toFixed(2);
  });

  readonly currentTimeTile: Signal<SzHfTime> = computed((): SzHfTime => {
    const label: string = this.translate.instant('session.hf.timeButtonLabel');
    const t: string | null = this.currentTimeLabel();
    return {
      label,
      value: t === null ? '-.--' : t,
      invalid: t === null
    };
  });

  readonly liveHitFactor: Signal<number | null> = computed(() =>
    computeHitFactor(zonesToHitFactorInput(this.currentZones(), this.currentTimeSeconds()))
  );

  readonly canSaveRun: Signal<boolean> = computed(() => {
    const t: number | null = this.currentTimeSeconds();
    if (t === null || t <= 0) return false;
    return this.sessionDrill().shotsCount !== null;
  });

  readonly rows: Signal<readonly SessionRunRow[]> = computed((): readonly SessionRunRow[] =>
    buildRunRows(this.sessionDrill(), this.translate.instant('session.numpad.repsUnit'))
  );

  constructor() {
    this.resetZonesWhenShotsCountChanges();
  }

  onCellClick(zone: SzHfZoneKey): void {
    if (this.sessionDrill().shotsCount === null) return;
    this.currentZones.update((zones: readonly SzHfZoneKey[]) => allocateShotTo(zones, zone));
  }

  openTimeSheet(): void {
    this.timeSheetResetTrigger.update((v: number) => v + 1);
    this.timeSheetOpen.set(true);
  }

  closeTimeSheet(): void {
    this.timeSheetOpen.set(false);
  }

  onTimeSheetSubmit(payload: RunInput): void {
    if (payload.timeSeconds !== undefined && payload.timeSeconds !== null) {
      this.currentTimeSeconds.set(payload.timeSeconds);
    }
    this.closeTimeSheet();
  }

  saveRun(): void {
    if (!this.canSaveRun()) return;
    const instanceId: string = this.sessionDrill().instanceId;
    const counts: Readonly<Record<SzHfZoneKey, number>> = countByZone(this.currentZones());
    const payload: RunInput = {
      hitsA: counts.A,
      hitsC: counts.C,
      hitsD: counts.D,
      hitsM: counts.M,
      hitsNS: counts.NS,
      timeSeconds: this.currentTimeSeconds()
    };
    const editing: RunViewModel | null = this.editingRun();
    if (editing !== null) {
      this.runHandler.update(editing.id, payload);
      this.editingRun.set(null);
    } else {
      this.runHandler.add(instanceId, payload);
    }
    this.resetCurrentRun();
  }

  loadRunForEdit(runId: string): void {
    const run: RunViewModel | null = this.sessionDrill().runs.find((r: RunViewModel) => r.id === runId) ?? null;
    if (run === null) return;
    const shotsCount: number | null = this.sessionDrill().shotsCount;
    if (shotsCount === null) return;
    const zones: SzHfZoneKey[] = [
      ...Array.from({ length: run.hitsA ?? 0 }, (): SzHfZoneKey => 'A'),
      ...Array.from({ length: run.hitsC ?? 0 }, (): SzHfZoneKey => 'C'),
      ...Array.from({ length: run.hitsD ?? 0 }, (): SzHfZoneKey => 'D'),
      ...Array.from({ length: run.hitsM ?? 0 }, (): SzHfZoneKey => 'M'),
      ...Array.from({ length: run.hitsNS ?? 0 }, (): SzHfZoneKey => 'NS')
    ];
    while (zones.length < shotsCount) zones.push('A');
    this.currentZones.set(zones.slice(0, shotsCount));
    this.currentTimeSeconds.set(run.timeSeconds);
    this.editingRun.set(run);
  }

  cancelEdit(): void {
    this.editingRun.set(null);
    this.resetCurrentRun();
  }

  deleteRow(runId: string): void {
    this.confirmDialog
      .confirm$({
        header: this.translate.instant('session.run.deleteConfirm'),
        okLabel: this.translate.instant('session.run.deleteAction'),
        destructive: true
      })
      .pipe(
        filter((ok: boolean): boolean => ok),
        take(1)
      )
      .subscribe((): void => {
        this.runHandler.delete(runId);
        if (this.editingRun()?.id === runId) this.cancelEdit();
      });
  }

  private resetCurrentRun(): void {
    const shotsCount: number | null = this.sessionDrill().shotsCount;
    this.currentZones.set(shotsCount === null ? [] : initShotAllocation(shotsCount));
    this.currentTimeSeconds.set(null);
  }

  private resetZonesWhenShotsCountChanges(): void {
    effect(() => {
      const shotsCount: number | null = this.sessionDrill().shotsCount;
      if (this.editingRun() !== null) return;
      this.currentZones.set(shotsCount === null ? [] : initShotAllocation(shotsCount));
    });
  }
}
